import React, { useMemo } from 'react'
import {
  DashboardInvestmentsGraphs,
  DashboardSummary,
  OpportunityDetails,
  DashboardInvestmentTransactions,
} from '../../lib/types/propsType_v1'
import { dashboardGraphFilter } from '../../lib/utils/message'
import { displayDollar } from '../../lib/utils/helpers'
import { ASSET_CLASSES } from '../../lib/utils/constants'
import { PerformanceChart } from './components/PerformanceChart'
import { Stats } from './components/Stats'
import { getSharePricing } from '../invest-v2/invest-v2-share-order'
import { TransactionsTable } from './components/TransactionsTable'
import {
  centsToCurrency,
  buildPerformanceSeries,
  isUnderlyingExposureMode,
  formatUnitsOrSharesDisplay,
  PENDING_SHARES_ALLOTMENT_SUBTEXT,
} from './dashboard-v2.utils'

type PresenterProps = {
  investorName: string
  opportunity?: OpportunityDetails
  summary: DashboardSummary
  currentNavValue: number
  graphFilter: { text: string; value: string }
  onChangeFilter: (value: string) => void
  onBackToDashboard: () => void
  graphSeries: DashboardInvestmentsGraphs
  transactions: DashboardInvestmentTransactions[]
  transactionsPage: number
  transactionsPagination: { total: number; limit: number; count: number; offset: number }
  onChangeTransactionsPage: (page: number) => void
  onTransactionClick?: (transaction: DashboardInvestmentTransactions) => void
}

export const DashboardV2SpecificInvestmentPresenter: React.FC<PresenterProps> = ({
  opportunity,
  summary,
  currentNavValue,
  graphFilter,
  onChangeFilter,
  onBackToDashboard,
  graphSeries,
  transactions,
  onTransactionClick,
}) => {
  const perfSeriesCents = useMemo(() => buildPerformanceSeries(graphSeries), [graphSeries])
  const formatInterest = (value?: string) => {
    if (!value) return '-'
    const numeric = Number(String(value).replace(/[^0-9.-]/g, ''))
    if (Number.isNaN(numeric)) return value.includes('%') ? value : `${value}%`
    return `${numeric.toFixed(2)}%`
  }
  const ticketSizeText = useMemo(() => {
    const n = Number(opportunity?.ticketSize)
    if (Number.isNaN(n)) return opportunity?.ticketSize || '-'
    return centsToCurrency(n)
  }, [opportunity?.ticketSize])
  const showTargetInterest =
    opportunity?.assetClass === ASSET_CLASSES.FIXED_INCOME ||
    opportunity?.assetClass === ASSET_CLASSES.TRADE_FINANCE
  const metaItems = [
    showTargetInterest && { label: 'Target Interest', value: formatInterest(opportunity?.irr) },
    { label: 'Ticket Size', value: ticketSizeText },
    { label: 'Jurisdiction', value: opportunity?.location || '-' },
    opportunity?.metadata?.securities_offering && {
      label: 'Securities Offering',
      value: opportunity.metadata.securities_offering,
    },
    opportunity?.metadata?.underlying_fund && {
      label: 'Underlying Security',
      value: opportunity.metadata.underlying_fund,
    },
  ].filter(Boolean) as { label: string; value: string }[]
  const goalAmountCents = useMemo(() => {
    const n = Number(opportunity?.raiseGoal)
    return Number.isFinite(n) ? n : 0
  }, [opportunity?.raiseGoal])
  const reachedAmountCents = useMemo(() => {
    const n = Number(opportunity?.committedAmount)
    return Number.isFinite(n) ? n : 0
  }, [opportunity?.committedAmount])
  const goalProgressPct = useMemo(() => {
    if (!goalAmountCents) return 0
    return Math.max(0, Math.min(100, (reachedAmountCents / goalAmountCents) * 100))
  }, [goalAmountCents, reachedAmountCents])
  const perfSeriesDollars = useMemo(() => {
    const raw = perfSeriesCents.map((pt) => ({ ...pt, value: pt.value / 100 }))
    if (raw.length === 1) {
      const only = raw[0]
      const d = new Date(only.date)
      d.setDate(d.getDate() - 1)
      return [{ ...only, date: d.toISOString().slice(0, 10) }, only]
    }
    return raw
  }, [perfSeriesCents])
  const currentValueText = centsToCurrency(perfSeriesCents?.[perfSeriesCents.length - 1]?.value)

  const exposureMode = isUnderlyingExposureMode(
    summary?.underlyingExposure,
    summary?.underlyingExposureDescription,
  )
  const showExposureCard = typeof summary?.underlyingExposure === 'string'
  // Share-priced deals (CB-239) show shares and share price instead of units and NAV.
  const shareMode = exposureMode || Boolean(getSharePricing(opportunity))
  const totalUnits = summary?.totalUnits ?? 0
  const unitsAllotted = totalUnits > 0
  const sharesPending = exposureMode && !unitsAllotted
  const sharesDisplay = formatUnitsOrSharesDisplay(totalUnits, { exposureMode: shareMode })
  const hasSharePrice = unitsAllotted && typeof currentNavValue === 'number' && currentNavValue > 0
  let sharePriceDisplay: string
  if (hasSharePrice) {
    sharePriceDisplay = displayDollar(String(currentNavValue), 'USD', 2, true, shareMode)
  } else if (shareMode) {
    sharePriceDisplay = '—'
  } else {
    sharePriceDisplay = displayDollar(String(currentNavValue || 0), 'USD', 2, true)
  }
  const unitsLabel = shareMode ? 'No. of Shares' : 'Total Units'
  let priceLabel = 'Applicable NAV'
  if (shareMode) {
    priceLabel = 'Share Price'
  }
  const pendingSubtext = sharesPending ? PENDING_SHARES_ALLOTMENT_SUBTEXT : null
  const statsGridClass = showExposureCard
    ? 'dashboard-v2__stats-grid dashboard-v2__stats-grid--three'
    : 'dashboard-v2__stats-grid'

  return (
    <div className='dashboard-v2'>
      <div className='dashboard-v2__back-wrap'>
        <button type='button' className='secondary small' onClick={onBackToDashboard}>
          Back to Dashboard
        </button>
      </div>
      <section className='dashboard-v2__top-grid'>
        <div className='dashboard-v2__profile-card dashboard-v2__profile-card--compact'>
          <div className='profile-row profile-row--tight'>
            <div className='opportunity-head'>
              <span className='muted muted--inline'>Opportunity</span>
              <h3 title={opportunity?.projectName}>{opportunity?.projectName || '-'}</h3>
              <span className='muted'>{opportunity?.assetClass || '-'}</span>
            </div>
          </div>
          {/* Temporarily hidden; flip back to true to restore the committed goal progress block. */}
          {/* <>
              <div className='limit-row goal-progress'>
                <span>Goal Progress</span>
                <strong className='goal-progress__value'>{goalProgressPct.toFixed(2)}%</strong>
              </div>
              <div className='progress progress--tight'>
                <div style={{ width: `${goalProgressPct}%` }} />
              </div>
              <div className='muted goal-progress__amount'>
                {centsToCurrency(reachedAmountCents)} / {centsToCurrency(goalAmountCents)}
              </div>
            </>
       */}
          {/* 4 items -> 2 columns; 5 items -> 3 columns then 2 */}
          <div
            className={`progress-meta progress-meta--compact-grid${
              metaItems.length === 5 ? ' progress-meta--three-col' : ''
            }`}
          >
            {metaItems.map(({ label, value }) => (
              <div key={label}>
                <small>{label}</small>
                <strong>{value}</strong>
              </div>
            ))}
          </div>
        </div>
        <Stats summary={summary} />
      </section>
      <section className={statsGridClass} data-testid='opportunity-units-nav-stats'>
        <article className='dashboard-v2__stat-card'>
          <div className='icon'>📦</div>
          <p className='muted'>{unitsLabel}</p>
          <h3 className='value-fit'>{sharesDisplay}</h3>
          {pendingSubtext && <p className='stat-note'>{pendingSubtext}</p>}
        </article>
        <article className='dashboard-v2__stat-card'>
          <div className='icon'>🧭</div>
          <p className='muted'>{priceLabel}</p>
          <h3 className='value-fit'>{sharePriceDisplay}</h3>
          {pendingSubtext && <p className='stat-note'>{pendingSubtext}</p>}
        </article>
        {showExposureCard && (
          <article className='dashboard-v2__stat-card' data-testid='underlying-exposure-card'>
            <div className='icon'>📊</div>
            <p className='muted'>Underlying Exposure</p>
            <h3 className='value-fit'>{summary.underlyingExposure}</h3>
            {summary.underlyingExposureDescription && (
              <p className='stat-note'>{summary.underlyingExposureDescription}</p>
            )}
          </article>
        )}
      </section>

      <PerformanceChart
        title='Investment Performance'
        filters={dashboardGraphFilter}
        selected={graphFilter.value}
        onChange={onChangeFilter}
        series={perfSeriesDollars}
        currentValueText={currentValueText}
        lastUpdatedText={new Date().toLocaleString()}
        transactions={transactions}
      />

      <TransactionsTable transactions={transactions} onTransactionClick={onTransactionClick} />
    </div>
  )
}
